// ============================================
// GANTT CONFIG HELPERS
// ============================================
//
// t17: pure helpers around the Gantt view's persisted config shape.
// Kept in their own module so gantt-view.tsx (state owner) and
// gantt-settings-panel.tsx (config editor) share one definition of
// "what counts as a valid timeScale / groupBy / showSprints".
//
// The shape is intentionally aligned with the backend's viewConfigSchema
// (backend/src/schemas/view.ts) so the saved view round-trip is loss-
// free.

import type {
    Task,
    TaskStatus,
    ViewConfig,
} from '@/lib/schemas';

// The four supported time scales. The backend's viewConfigSchema enum
// has the same literal set.
export const GANTT_TIME_SCALES = ['day', 'week', 'month', 'auto'] as const;
export type GanttTimeScale = (typeof GANTT_TIME_SCALES)[number];

export const GANTT_GROUP_BY = ['status', 'assignee', 'none'] as const;
export type GanttGroupBy = (typeof GANTT_GROUP_BY)[number];

// Defaults: t14 spec calls for timeScale='auto', showSprints=true,
// groupBy='status'. R1 / t17 fix flips `groupBy` to 'none' for the
// auto-seeded 'All tasks' Gantt view — see the matching comment on
// buildDefaultGanttViewConfig() in backend/src/schemas/view.ts. The
// frontend uses the same default so an empty stored config (which is
// what the lazy auto-seed in project-detail.tsx persists) renders as
// a single lane sorted by startDate instead of five status lanes.
// User-created views can still flip to 'status' / 'assignee' via the
// settings popover.
//
// R3 / t17: `showDateless` defaults to `false` so dateless tasks
// (tasks with no startDate and no endDate) are hidden by default.
// R1/R2 pinned them at the timeline origin (barLeft=0) which
// cluttered the visible bars. They live in the sort anyway (always
// LAST within their group) so flipping the toggle on brings them
// right back, just at the bottom of the group.
export const DEFAULT_GANTT_TIME_SCALE: GanttTimeScale = 'auto';
export const DEFAULT_GANTT_GROUP_BY: GanttGroupBy = 'none';
export const DEFAULT_GANTT_SHOW_SPRINTS = true;
export const DEFAULT_GANTT_SHOW_DATELESS = false;

// The active view's normalised Gantt config — every field has a
// concrete default so downstream code never has to deal with `undefined`.
export interface NormalisedGanttConfig {
    timeScale: GanttTimeScale;
    filters: ViewConfig['filters'];
    showSprints: boolean;
    groupBy: GanttGroupBy;
    showDateless: boolean;
}

// Read a SavedView's stored config into a NormalisedGanttConfig.
// Unknown / missing values fall back to the same defaults
// buildDefaultGanttViewConfig() uses on the backend.
//
// R3 / t17: `showDateless` is read defensively (the backend's
// viewConfigSchema doesn't enforce it as a typed key — we cast to
// `any` for the read so an existing view without the field still
// normalises cleanly). When the backend schema gets extended we'll
// swap to the typed read.
export function normaliseGanttConfig(
    config: ViewConfig | undefined | null
): NormalisedGanttConfig {
    const timeScale = isTimeScale(config?.timeScale)
        ? config!.timeScale!
        : DEFAULT_GANTT_TIME_SCALE;
    const groupBy = isGroupBy(config?.ganttGroupBy)
        ? config!.ganttGroupBy!
        : DEFAULT_GANTT_GROUP_BY;
    const showSprints =
        typeof config?.showSprints === 'boolean'
            ? config!.showSprints!
            : DEFAULT_GANTT_SHOW_SPRINTS;
    const showDateless =
        typeof (config as any)?.showDateless === 'boolean'
            ? (config as any).showDateless
            : DEFAULT_GANTT_SHOW_DATELESS;
    return {
        timeScale,
        groupBy,
        showSprints,
        showDateless,
        filters: config?.filters ?? {},
    };
}

function isTimeScale(v: unknown): v is GanttTimeScale {
    return typeof v === 'string' && (GANTT_TIME_SCALES as readonly string[]).includes(v);
}

function isGroupBy(v: unknown): v is GanttGroupBy {
    return typeof v === 'string' && (GANTT_GROUP_BY as readonly string[]).includes(v);
}

// ============================================
// DATE RESOLUTION
// ============================================
//
// Resolve a task's start/end datetimes into Date objects. The backend
// (Prisma) writes ISO datetime strings for `startDate` / `endDate`
// (t14). Returns nulls when the task has no usable date — callers
// should drop the task from the timeline or render it as a marker.

// Shape returned by resolveTaskRange. `isPoint` is true when start ===
// end (a single-day task or a marker-only task with only one date).
export interface TaskRangeResolution {
    start: Date | null;
    end: Date | null;
    isPoint: boolean;
    hasAnyDate: boolean;
}

export function resolveTaskRange(task: Task): TaskRangeResolution {
    const startRaw = (task as any).startDate as string | null | undefined;
    const endRaw = (task as any).endDate as string | null | undefined;
    const start = startRaw ? new Date(startRaw) : null;
    const end = endRaw ? new Date(endRaw) : null;
    const hasAnyDate = Boolean(start || end);
    if (!hasAnyDate) return { start: null, end: null, isPoint: false, hasAnyDate: false };
    const finalStart = start ?? end!;
    const finalEnd = end ?? start!;
    return {
        start: finalStart,
        end: finalEnd,
        isPoint: finalStart.getTime() === finalEnd.getTime(),
        hasAnyDate: true,
    };
}

// ============================================
// GROUPING
// ============================================
//
// Build the ordered list of group rows for the active groupBy setting.
// Each group has a `key` (stable id for React keys) and a `label`
// (human-readable title rendered on the row header).

export interface GanttGroup {
    key: string;
    label: string;
}

export function buildGroups(
    tasks: Task[],
    groupBy: GanttGroupBy,
    statusLabel: (status: TaskStatus) => string,
    unassignedLabel: string
): GanttGroup[] {
    if (groupBy === 'none') {
        return [{ key: '__all__', label: '' }];
    }
    if (groupBy === 'status') {
        // Always render the five canonical statuses in their natural
        // order, even if a group is empty — keeps the Gantt layout
        // stable when a task moves between groups.
        return (['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'CANCELLED'] as TaskStatus[]).map(
            (s) => ({ key: `status:${s}`, label: statusLabel(s) })
        );
    }
    // assignee
    const seen = new Map<string, GanttGroup>();
    seen.set('__unassigned__', { key: '__unassigned__', label: unassignedLabel });
    for (const task of tasks) {
        if (!task.assignees || task.assignees.length === 0) continue;
        for (const a of task.assignees) {
            const key = `assignee:${a.userId}`;
            if (seen.has(key)) continue;
            seen.set(key, { key, label: a.user?.name || a.userId });
        }
    }
    return Array.from(seen.values());
}

export function groupOf(task: Task, groupBy: GanttGroupBy): string {
    if (groupBy === 'none') return '__all__';
    if (groupBy === 'status') return `status:${task.status}`;
    const first = task.assignees?.[0];
    if (!first) return '__unassigned__';
    return `assignee:${first.userId}`;
}

// ============================================
// TIME-SCALE RESOLUTION
// ============================================
//
// Convert the `auto` time scale to a concrete one based on the visible
// span. The thresholds mirror what feels right for a single-screen
// Gantt: <14 days → day, <90 days → week, else month.

const DAY_MS = 24 * 60 * 60 * 1000;

export function resolveTimeScale(
    requested: GanttTimeScale,
    visibleSpanMs: number
): Exclude<GanttTimeScale, 'auto'> {
    if (requested !== 'auto') return requested;
    const days = visibleSpanMs / DAY_MS;
    if (days <= 14) return 'day';
    if (days <= 90) return 'week';
    return 'month';
}

// Pixel width of a single unit at a given (concrete) time scale. The
// Gantt timeline multiplies this by the number of units to compute
// the grid width — Day=24, Week=120 (= 7 days), Month=180 (= 30
// days). Keeps most projects on a single screen with sensible grid
// cell widths.
export const TIME_SCALE_PX_PER_UNIT: Record<Exclude<GanttTimeScale, 'auto'>, number> = {
    day: 24,
    week: 120,
    month: 180,
};

// R2 fix: pixel width of ONE DAY at a given (concrete) time scale.
// Bars are positioned and sized in day-relative pixels (not
// unit-relative), so a 5-day task at week scale lives at
// `(5 * 120/7) ≈ 85px` from origin and spans `(5 * 120/7) ≈ 85px`,
// instead of being inflated to `(5 * 120) = 600px` (= 5 weeks). The
// grid columns stay at unit width (TIME_SCALE_PX_PER_UNIT above) so
// the day / week / month header still aligns with its cells.
export const TIME_SCALE_PX_PER_DAY: Record<Exclude<GanttTimeScale, 'auto'>, number> = {
    day: 24,
    week: 120 / 7,
    month: 180 / 30,
};

// How many days each unit represents at a given (concrete) time
// scale. Day=1, Week=7, Month≈30. Used for clamping + future label
// math.
export const TIME_SCALE_DAYS_PER_UNIT: Record<Exclude<GanttTimeScale, 'auto'>, number> = {
    day: 1,
    week: 7,
    month: 30,
};
